import { ArrowLeft, History, KeyRound } from "lucide-react";
import { Badge, BadgeProps } from "~/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import { OverviewTiles } from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { CeConfigDomain } from "~/types/interfaces/ce-config";
import { OlderVersionNotice } from "./OlderVersionNotice";
import {
  chartLabel,
  ConfigColumn,
  columnDisplayName,
  domainStats,
  filterBadge,
  FilterTone,
  isBreakdown,
  isExportable,
  isKeyColumn,
  isViewable,
} from "./config-view";

interface DomainDetailProps {
  domain: CeConfigDomain;
  versionNumber: number | null;
  isViewingOlder: boolean;
  onBack: () => void;
  onViewLatest: () => void;
}

const TONE_TO_VARIANT: Record<FilterTone, BadgeProps["variant"]> = {
  select: "info",
  numeric: "purple",
  date: "success",
  off: "muted",
};

const YesNo = ({ value }: { value: boolean }) =>
  value ? (
    <span className="text-foreground">Yes</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

export const DomainDetail = ({
  domain,
  versionNumber,
  isViewingOlder,
  onBack,
  onViewLatest,
}: DomainDetailProps) => {
  const columns: ConfigColumn[] = domain.columns ?? [];
  const isPrimary = Boolean(domain.primary);
  const stats = domainStats(columns, isPrimary);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={onBack}
          className="flex w-fit cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          All Tables
        </button>

        <div className="flex items-center gap-3">
          <span
            className={cn("size-4 shrink-0 rounded-full", !domain.icon && "bg-muted-foreground")}
            style={domain.icon ? { backgroundColor: domain.icon } : undefined}
            aria-hidden="true"
          />
          <h2 className="text-2xl font-semibold">{domain.name}</h2>
          {isPrimary && <Badge variant="secondary">Primary</Badge>}
          {isViewingOlder && versionNumber != null && (
            <Badge variant="warning" className="gap-1">
              <History className="size-3" aria-hidden="true" />
              Version {versionNumber} · older
            </Badge>
          )}
        </div>
      </div>

      {isViewingOlder && (
        <OlderVersionNotice onViewLatest={onViewLatest}>
          This is a previous configuration version, not the currently active one. Columns, filters,
          and exports may differ from the active version.
        </OlderVersionNotice>
      )}

      <div className="rounded-xl border border-border bg-muted/30 p-5">
        <OverviewTiles
          layout="stacked"
          tiles={[
            {
              label: "Source Table",
              content: domain.table,
            },
            { label: "Columns", content: `${stats.columnCount} configured` },
            { label: "Filters", content: `${stats.filterCount} columns` },
            { label: "Visualized", content: `${stats.chartCount} columns` },
            { label: "Viewable", content: `${stats.viewableCount} columns` },
            ...(isPrimary
              ? [{ label: "Breakdowns", content: `${stats.breakdownCount} of 2` }]
              : []),
            { label: "Exportable", content: `${stats.exportCount} columns` },
            { label: "Description", content: domain.description || "—" },
          ]}
        />
      </div>

      <div className="rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Column</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>Filter</TableHead>
              <TableHead>Chart</TableHead>
              <TableHead>Viewable</TableHead>
              <TableHead>Export</TableHead>
              <TableHead>Breakdown</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {columns.map((column) => {
              const badge = filterBadge(column);
              return (
                <TableRow key={column.column} className="[tbody_&]:hover:bg-transparent">
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-xs text-foreground">{column.column}</span>
                      {isKeyColumn(column, domain.table_key) && (
                        <Badge variant="purple" title="Table key">
                          <KeyRound className="size-3" aria-hidden="true" />
                          Key
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {columnDisplayName(column)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={TONE_TO_VARIANT[badge.tone]}>{badge.label}</Badge>
                  </TableCell>
                  <TableCell
                    className={cn(
                      chartLabel(column) === "Off" ? "text-muted-foreground" : "text-foreground"
                    )}
                  >
                    {chartLabel(column)}
                  </TableCell>
                  <TableCell>
                    <YesNo value={isViewable(column)} />
                  </TableCell>
                  <TableCell>
                    <YesNo value={isExportable(column)} />
                  </TableCell>
                  <TableCell>
                    <YesNo value={isPrimary && isBreakdown(column)} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
